//Nov5 RowComponent.js:

//May8 RowComponent
// === RowComponent.jsx  (May-02 – shrink-on-drag + proper flex grow) ========
import React, { memo, useMemo } from 'react';
import Box                     from '@mui/material/Box';
import DropZone                from './DropZone';
import DraggableItem           from './DraggableItem';
import { DropZoneTypes }       from './constants';
import { useFormLayout }       from '../../../contexts/FormLayoutContext';
import { useFieldMetadata }    from '../../../contexts/FieldMetadataContext';
import SubmitButtonComponent   from './SubmitButtonComponent';

const RowComponent = memo(({ rowKey, rowIndex }) => {
  /* ------------------------------------------------------------------ */
  /*                     context & helpers                              */
  /* ------------------------------------------------------------------ */
  const {
    layoutConfig,
    handleDrop,
    showDropZones,            // TRUE while drag is armed
    setShowDropZones,
    handleDragStart,
    handleDragEnd,
  } = useFormLayout();

  const { fieldsMetadata, isLoading } = useFieldMetadata();

  /* ------------------------------------------------------------------ */
  /* base drag affordance – runs every render */
  /* ------------------------------------------------------------------ */
  const fieldWrapperBase = useMemo(
    () => ({
      // ENTERPRISE:
      // Do NOT change transform at drag start.
      // HTML5 drag can cancel when the drag source or its ancestors change transforms.
      // Use paint-only affordances (box-shadow/outline) instead.
      boxShadow      : showDropZones ? '0 0 0 1px rgba(25,118,210,0.18) inset' : 'none',
      transition     : 'box-shadow 120ms ease',
      display        : 'flex',
      alignItems     : 'stretch',
    }),
    [showDropZones]
  );

  /* util: merge shrink transform + per-field flex rules */
  const computeWrapperStyle = (meta = {}) => {
    const isHeader = meta?.type === 'header';
    const isIcon   = meta?.allowedImageTypes?.includes?.('icon');
    const isBanner = meta?.allowedImageTypes?.includes?.('banner');

    return {
      ...fieldWrapperBase,
      minWidth  : 0,             // allow this flex item (column) to shrink
      /* flex behaviour identical to original DraggableItem semantics */
      flexGrow  : isIcon ? 0 : 1,
      flexShrink: isIcon ? 0 : 1,
      flexBasis : isIcon ? 'auto' : 0,
      width     : isBanner ? '100%' : 'auto',
       // Center header content vertically within the stretched column
       alignItems: isHeader ? 'center' : fieldWrapperBase.alignItems,

    };
  };

  /* ------------------------------------------------------------------ */
  /*                          early guards                              */
  /* ------------------------------------------------------------------ */
  const row         = layoutConfig[rowKey];
  const isSubmitRow = rowKey === 'submitRow';

  if (isLoading || !fieldsMetadata || Object.keys(fieldsMetadata).length === 0) {
    return null;
  }
  if (!row || rowIndex === undefined) {
    console.error(`[RowComponent] No row data for key ${rowKey}`);
    return null;
  }

  /* ------------------------------------------------------------------ */
  /*              dedicated rendering for submit row                    */
  /* ------------------------------------------------------------------ */
  if (isSubmitRow) {
    return (
      <Box
        style={{
          display        : 'flex',
          justifyContent : row[0].style.alignment || 'center',
          margin         : '20px 0',
        }}
      >
        <SubmitButtonComponent field={row[0]} />
      </Box>
    );
  }

  /* prune truly empty rows */
  if (row.length === 0) {
    console.warn(`[RowComponent] Row ${rowKey} is empty after deletion.`);
    return null;
  }

  /* ------------------------------------------------------------------ */
  /*                        main row markup                             */
  /* ------------------------------------------------------------------ */
  return (
    <Box
      key={`row-${rowIndex}`}
      style={{
        display       : 'flex',
        flexDirection : 'column',
        marginBottom  : 0,
        zIndex        : 1,
      }}
    >
      {/* ── ABOVE zone (only first row) ─────────────────────────────── */}
      {rowIndex === 0 && (
        <DropZone
          key={`drop-above-${rowIndex}`}
          onDrop={handleDrop}
          type={DropZoneTypes.ABOVE}
          rowIndex={0}
          fieldIndex={0}
          showDropZones={showDropZones}
        />
      )}


      {/* ── fields row (LEFT zone + fields) ─────────────────────────── */}
      <Box style={{ display: 'flex', alignItems: 'stretch', width: '100%', minWidth: 0 }}>

        {/* leading LEFT zone */}
        <DropZone
          key={`drop-start-${rowIndex}`}
          onDrop={handleDrop}
          type={DropZoneTypes.LEFT}
          rowIndex={rowIndex}
          fieldIndex={0}
          showDropZones={showDropZones}
        />

        {/* each field & its trailing RIGHT zone */}
        {row.map((field, fieldIndex) => {
          const meta = fieldsMetadata[field.id];
          if (!meta) {
            console.warn(`[RowComponent] Metadata missing for field ${field.id}`);
            return null;
          }

          return (
          <React.Fragment key={field.id}>
            <Box style={computeWrapperStyle(meta)}>
              <DraggableItem
                id={field.id}
                label={meta.label || 'Unnamed Field'}
                type={meta.type || 'unknown'}
                required={meta.required || false}
                index={fieldIndex}
                rowIndex={rowIndex}
                handleDrop={handleDrop}
                fieldMetadata={meta}
                showDropZones={showDropZones}
                setShowDropZones={setShowDropZones}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
              />
            </Box>

            <DropZone
              onDrop={handleDrop}
              type={DropZoneTypes.RIGHT}
              rowIndex={rowIndex}
              fieldIndex={fieldIndex + 1}
              showDropZones={showDropZones}
            />
          </React.Fragment>
          );
        })}
      </Box>

      {/* ── BELOW zone (every row) ──────────────────────────────────── */}
      <DropZone
        key={`drop-below-${rowIndex}`}
        onDrop={handleDrop}
        type={DropZoneTypes.BELOW}
        rowIndex={rowIndex}
        fieldIndex={0}
        showDropZones={showDropZones}
      />

    </Box>
  );
});

export default RowComponent;
